import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import BackButton from '../components/BackButton';
import AdBanner from '../components/AdBanner';
import Icon from '../components/Icon';
import './styles/ContentPages.css';

const YkiGuidePage = () => {
  const { i18n } = useTranslation();
  const isRu = i18n.language === 'ru';

  return (
    <div className="content-page">
      <Helmet>
        <title>YKI Test Guide: Levels, Structure & Registration | YKI Testi</title>
        <meta name="description" content="Complete guide to the YKI Finnish language proficiency test. Learn about perustaso, keskitaso and ylin taso levels, test structure, scoring, registration and citizenship requirements." />
        <link rel="canonical" href="https://ykitesti.fi/yki-guide" />
      </Helmet>
      <BackButton to="/" label={isRu ? 'Главная' : 'Home'} />
      <h1 className="content-page-title">
        {isRu ? 'Тест YKI — Полное руководство' : 'YKI Test - Complete Guide'}
      </h1>
      <p className="content-page-subtitle">
        {isRu
          ? 'Всё, что нужно знать об экзамене по финскому языку'
          : 'Everything you need to know about the Finnish language proficiency test'}
      </p>

      <AdBanner slot="guide-top" />

      <article className="content-article">
        <section className="content-section">
          <h2>{isRu ? 'Что такое тест YKI?' : 'What is the YKI Test?'}</h2>
          <p>
            {isRu
              ? 'Тест YKI (Yleiset kielitutkinnot) — официальный экзамен по финскому языку, который проводит Национальное агентство образования Финляндии (Opetushallitus). Это наиболее широко признанный сертификат владения финским языком в Финляндии, используемый при подаче заявлений на гражданство, для трудоустройства и в академических целях.'
              : 'The YKI test (Yleiset kielitutkinnot) is the official Finnish language proficiency test administered by the Finnish National Agency for Education (Opetushallitus). It is the most widely recognized certification of Finnish language skills in Finland, used for citizenship applications, employment, and academic purposes.'}
          </p>
          <p>
            {isRu
              ? 'Тест оценивает вашу способность понимать финский язык и использовать его в реальных жизненных ситуациях. Он охватывает чтение, письмо, аудирование и устную речь. Каждый навык оценивается отдельно, и вы получаете балл за каждый раздел.'
              : 'The test evaluates your ability to understand and produce Finnish in real-life situations. It covers reading comprehension, writing, listening comprehension, and speaking. Each skill area is tested separately, and you receive a score for each section.'}
          </p>
        </section>

        <section className="content-section">
          <h2>{isRu ? 'Уровни теста YKI' : 'YKI Test Levels'}</h2>
          <p>
            {isRu
              ? 'Тест YKI проводится на трёх уровнях, соответствующих Общеевропейским компетенциям владения иностранным языком (CEFR):'
              : 'The YKI test is offered at three levels, aligned with the Common European Framework of Reference (CEFR):'}
          </p>

          <div className="level-cards">
            <div className="level-card">
              <div className="level-badge" style={{ background: 'var(--color-success-dim)', color: 'var(--color-success)' }}>Perustaso</div>
              <h3>{isRu ? 'Базовый уровень (A1–A2)' : 'Basic Level (A1-A2)'}</h3>
              <p>
                {isRu
                  ? 'Базовый уровень проверяет вашу способность справляться с простыми, повседневными ситуациями общения. Вы должны понимать распространённые слова и фразы, представляться, задавать и отвечать на простые вопросы на личные темы, а также писать короткие сообщения.'
                  : 'The basic level tests your ability to handle simple, everyday communication situations. You should be able to understand common words and phrases, introduce yourself, ask and answer simple questions about personal topics, and write short messages.'}
              </p>
              <ul>
                <li>{isRu ? 'Понимать знакомые слова и основные фразы' : 'Understand familiar words and basic phrases'}</li>
                <li>{isRu ? 'Общаться в простых, рутинных ситуациях' : 'Communicate in simple, routine tasks'}</li>
                <li>{isRu ? 'Рассказывать о себе и ближайшем окружении' : 'Describe your background and immediate environment'}</li>
                <li>{isRu ? 'Писать короткие, простые заметки и сообщения' : 'Write short, simple notes and messages'}</li>
              </ul>
            </div>

            <div className="level-card">
              <div className="level-badge" style={{ background: 'var(--color-writing-dim)', color: 'var(--color-writing)' }}>Keskitaso</div>
              <h3>{isRu ? 'Средний уровень (B1–B2)' : 'Intermediate Level (B1-B2)'}</h3>
              <p>
                {isRu
                  ? 'Средний уровень — наиболее часто сдаваемый тест YKI. Достижение уровня 3 или 4 на этом уровне является обязательным для получения финского гражданства. Необходимо справляться с большинством ситуаций, возникающих при жизни в Финляндии.'
                  : 'The intermediate level is the most commonly taken YKI test. Achieving level 3 or 4 at this level is required for Finnish citizenship. You need to handle most situations that arise while living in Finland.'}
              </p>
              <ul>
                <li>{isRu ? 'Понимать основное содержание чёткой стандартной речи' : 'Understand the main points of clear standard speech'}</li>
                <li>{isRu ? 'Справляться с большинством ситуаций во время поездки или проживания в Финляндии' : 'Deal with most situations while travelling or living in Finland'}</li>
                <li>{isRu ? 'Составлять связные тексты на знакомые темы' : 'Produce simple connected text on familiar topics'}</li>
                <li>{isRu ? 'Описывать опыт, события и кратко обосновывать своё мнение' : 'Describe experiences, events, and briefly give reasons for opinions'}</li>
              </ul>
              <div className="content-callout">
                <strong>{isRu ? 'Требование для финского гражданства:' : 'Finnish Citizenship Requirement:'}</strong>{' '}
                {isRu
                  ? 'Для подачи заявления на финское гражданство, как правило, необходимо сдать тест YKI keskitaso на уровне 3 или выше по всем четырём навыкам (чтение, письмо, аудирование, говорение).'
                  : 'To apply for Finnish citizenship, you typically need to pass the YKI keskitaso test at level 3 or higher in all four skill areas (reading, writing, listening, speaking).'}
              </div>
            </div>

            <div className="level-card">
              <div className="level-badge" style={{ background: 'var(--color-vocab-dim)', color: 'var(--color-vocab)' }}>Ylin taso</div>
              <h3>{isRu ? 'Продвинутый уровень (C1–C2)' : 'Advanced Level (C1-C2)'}</h3>
              <p>
                {isRu
                  ? 'Продвинутый уровень демонстрирует свободное владение языком, близкое к родному. Как правило, он требуется для академических должностей, определённых профессиональных ролей и обучения в финских университетах.'
                  : 'The advanced level demonstrates near-native fluency. This level is typically required for academic positions, certain professional roles, and advanced studies at Finnish universities.'}
              </p>
              <ul>
                <li>{isRu ? 'Понимать широкий спектр сложных, объёмных текстов' : 'Understand a wide range of demanding, longer texts'}</li>
                <li>{isRu ? 'Выражать мысли свободно и спонтанно' : 'Express yourself fluently and spontaneously'}</li>
                <li>{isRu ? 'Гибко использовать язык в социальных, академических и профессиональных целях' : 'Use language flexibly for social, academic, and professional purposes'}</li>
                <li>{isRu ? 'Создавать чёткие, хорошо структурированные, подробные тексты на сложные темы' : 'Produce clear, well-structured, detailed text on complex subjects'}</li>
              </ul>
            </div>
          </div>
        </section>

        <AdBanner slot="guide-mid" />

        <section className="content-section">
          <h2>{isRu ? 'Структура теста' : 'Test Structure'}</h2>
          <p>{isRu ? 'Каждый уровень теста YKI состоит из четырёх частей:' : 'Each YKI test level consists of four parts:'}</p>

          <div className="info-grid">
            <div className="info-card">
              <span className="info-card-icon"><Icon name="book" size={28} /></span>
              <h4>{isRu ? 'Чтение' : 'Reading Comprehension'}</h4>
              <p>
                {isRu
                  ? 'Вы читаете различные тексты (объявления, статьи, инструкции) и отвечаете на вопросы по ним. Тексты усложняются на более высоких уровнях.'
                  : 'You read various texts (advertisements, articles, instructions) and answer questions about them. The texts become more complex at higher levels.'}
              </p>
            </div>
            <div className="info-card">
              <span className="info-card-icon"><Icon name="pen" size={28} /></span>
              <h4>{isRu ? 'Письмо' : 'Writing'}</h4>
              <p>
                {isRu
                  ? 'Вы пишете тексты по заданным темам. На базовом уровне это может быть простое сообщение. На более высоких уровнях вы пишете официальные письма или аргументативные эссе.'
                  : 'You write texts based on given prompts. At the basic level, this might be a simple message. At higher levels, you write formal letters or argumentative essays.'}
              </p>
            </div>
            <div className="info-card">
              <span className="info-card-icon"><Icon name="headphones" size={28} /></span>
              <h4>{isRu ? 'Аудирование' : 'Listening Comprehension'}</h4>
              <p>
                {isRu
                  ? 'Вы слушаете записи разговоров, объявлений и новостей, а затем отвечаете на вопросы. На базовом уровне аудио воспроизводится дважды, на продвинутом — один раз.'
                  : 'You listen to recordings of conversations, announcements, and news, then answer questions. Audio is played twice at the basic level, once at advanced.'}
              </p>
            </div>
            <div className="info-card">
              <span className="info-card-icon"><Icon name="mic" size={28} /></span>
              <h4>{isRu ? 'Говорение' : 'Speaking'}</h4>
              <p>
                {isRu
                  ? 'Вы выполняете устные задания, включая монологи и диалоги. Темы варьируются от повседневной жизни на базовом уровне до дискуссий и презентаций на продвинутом.'
                  : 'You participate in spoken tasks including monologues and dialogues. Topics range from daily life at the basic level to debates and presentations at advanced.'}
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>{isRu ? 'Система оценивания' : 'Scoring System'}</h2>
          <p>{isRu ? 'Каждый навык оценивается по шкале от 1 до 6:' : 'Each skill area is scored on a scale of 1 to 6:'}</p>
          <div className="score-table">
            <div className="score-row">
              <span className="score-level">{isRu ? 'Уровень 1–2' : 'Level 1-2'}</span>
              <span className="score-desc">
                {isRu
                  ? 'Ниже тестируемого уровня (Perustaso: A1, Keskitaso: ниже B1)'
                  : 'Below the level tested (Perustaso: A1, Keskitaso: under B1)'}
              </span>
            </div>
            <div className="score-row">
              <span className="score-level">{isRu ? 'Уровень 3' : 'Level 3'}</span>
              <span className="score-desc">
                {isRu
                  ? 'Соответствует нижней границе (Perustaso: A2, Keskitaso: B1, Ylin: C1)'
                  : 'Meets the lower threshold (Perustaso: A2, Keskitaso: B1, Ylin: C1)'}
              </span>
            </div>
            <div className="score-row">
              <span className="score-level">{isRu ? 'Уровень 4' : 'Level 4'}</span>
              <span className="score-desc">
                {isRu
                  ? 'Соответствует верхней границе (Perustaso: A2+, Keskitaso: B2, Ylin: C2)'
                  : 'Meets the upper threshold (Perustaso: A2+, Keskitaso: B2, Ylin: C2)'}
              </span>
            </div>
            <div className="score-row">
              <span className="score-level">{isRu ? 'Уровень 5–6' : 'Level 5-6'}</span>
              <span className="score-desc">
                {isRu
                  ? 'Превышает тестируемый уровень (выше диапазона уровня)'
                  : 'Exceeds the level tested (above the level\'s range)'}
              </span>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>{isRu ? 'Как зарегистрироваться' : 'How to Register'}</h2>
          <p>
            {isRu
              ? 'Тесты YKI проводятся несколько раз в год в различных местах по всей Финляндии. Для регистрации:'
              : 'YKI tests are organized several times a year at various locations across Finland. To register:'}
          </p>
          <ol className="content-ol">
            <li>
              {isRu ? (
                <>Зайдите на официальный портал регистрации YKI по адресу <strong>yki.opintopolku.fi</strong></>
              ) : (
                <>Visit the official YKI registration portal at <strong>yki.opintopolku.fi</strong></>
              )}
            </li>
            <li>{isRu ? 'Создайте учётную запись или войдите в систему' : 'Create an account or log in'}</li>
            <li>{isRu ? 'Выберите уровень теста и предпочтительное место проведения' : 'Choose your test level and preferred location'}</li>
            <li>{isRu ? 'Оплатите регистрационный взнос (в настоящее время около 100–130 евро в зависимости от уровня)' : 'Pay the registration fee (currently around 100-130 EUR depending on the level)'}</li>
            <li>{isRu ? 'Получите подтверждение и подготовьтесь к дате тестирования' : 'Receive confirmation and prepare for your test date'}</li>
          </ol>
          <p>
            {isRu
              ? 'Регистрация обычно открывается за 1–2 месяца до каждого теста. Места занимаются быстро, особенно в Хельсинки и других крупных городах, поэтому регистрируйтесь заранее.'
              : 'Registration typically opens 1-2 months before each test date. Places fill up quickly, especially in Helsinki and other major cities, so register early.'}
          </p>
        </section>

        <section className="content-section">
          <h2>{isRu ? 'Начните подготовку прямо сейчас' : 'Start Preparing Now'}</h2>
          <p>
            {isRu
              ? 'Приложение YKI Testi поможет вам подготовиться ко всем уровням теста YKI с карточками словарного запаса, упражнениями по письму и практикой устной речи. Начните подготовку уже сегодня!'
              : 'YKI Testi app helps you prepare for all levels of the YKI test with vocabulary flashcards, writing exercises, and speaking practice. Start your preparation today!'}
          </p>
          <div className="content-cta-row">
            <Link to="/vocabulary" className="content-cta">
              {isRu ? 'Начать изучать слова' : 'Start Learning Vocabulary'}
            </Link>
            <Link to="/writing" className="content-cta content-cta-secondary">
              {isRu ? 'Тренировать письмо' : 'Practice Writing'}
            </Link>
          </div>
        </section>
      </article>

      <AdBanner slot="guide-bottom" />
    </div>
  );
};

export default YkiGuidePage;
